import React, { useState, useEffect } from 'react';
import { FaCheckCircle } from 'react-icons/fa';
import './session-summary.scss';

export interface SessionSummaryProps {
  embedded?: boolean;
  specialtyName?: string;
  topicName?: string;
  onClose?: () => void;
}

export const SessionSummary: React.FC<SessionSummaryProps> = ({
  embedded = false,
  specialtyName,
  topicName,
  onClose
}) => {
  const [ipsScore, setIpsScore] = useState<number | null>(null);
  const [dataGatheringScore, setDataGatheringScore] = useState<number | null>(null);
  const [diagnosisMgtScore, setDiagnosisMgtScore] = useState<number | null>(null);
  const [areasForImprovement, setAreasForImprovement] = useState<string>('');
  const [isSaved, setIsSaved] = useState(false);

  const calculateTotalScore = () => {
    let total = 0;
    if (ipsScore !== null) total += ipsScore;
    if (dataGatheringScore !== null) total += dataGatheringScore;
    if (diagnosisMgtScore !== null) total += diagnosisMgtScore;
    return total;
  };

  const handleSave = () => {
    const summary = {
      id: `${Date.now()}`,
      specialtyName,
      topicName,
      ipsScore,
      dataGatheringScore,
      diagnosisMgtScore,
      areasForImprovement,
      timestamp: new Date().toISOString()
    };

    const existingSummaries = JSON.parse(localStorage.getItem('sessionSummaries') || '[]');
    existingSummaries.push(summary);
    localStorage.setItem('sessionSummaries', JSON.stringify(existingSummaries));

    setIsSaved(true);
    alert('Session summary saved!');
    if (onClose) onClose();
  };

  useEffect(() => {
    if (isSaved) {
      setIsSaved(false);
      if (onClose) onClose();
    }
  }, [isSaved, onClose]);

  const totalScore = calculateTotalScore();

  return (
    <div className="session-summary-page">
      <div className="session-summary-content">
        <h2>Session Summary</h2>

        <div className="session-details">
          <p><FaCheckCircle className="checkmark-icon" />{specialtyName} - {topicName}</p>
        </div>

        <div className="session-summary-form">
          <div className="scores-grid">
            <div className="form-group">
              <label htmlFor="ipsScore">IPS</label>
              <input
                type="number"
                id="ipsScore"
                min="0"
                max="4"
                value={ipsScore === null ? '' : ipsScore}
                onChange={(e) => setIpsScore(Number(e.target.value))}
              /> / 4
            </div>

            <div className="form-group">
              <label htmlFor="dataGatheringScore">Data Gathering</label>
              <input
                type="number"
                id="dataGatheringScore"
                min="0"
                max="4"
                value={dataGatheringScore === null ? '' : dataGatheringScore}
                onChange={(e) => setDataGatheringScore(Number(e.target.value))}
              /> / 4
            </div>

            <div className="form-group">
              <label htmlFor="diagnosisMgtScore">Diag & Mgt</label>
              <input
                type="number"
                id="diagnosisMgtScore"
                min="0"
                max="4"
                value={diagnosisMgtScore === null ? '' : diagnosisMgtScore}
                onChange={(e) => setDiagnosisMgtScore(Number(e.target.value))}
              /> / 4
            </div>

            <div className="form-group total-score">
              <label htmlFor="totalScore">Total</label>
              <input
                type="number"
                id="totalScore"
                value={totalScore}
                readOnly
              /> / 12
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="areasForImprovement">Areas for Improvement</label>
            <textarea
              id="areasForImprovement"
              value={areasForImprovement}
              onChange={(e) => setAreasForImprovement(e.target.value)}
              placeholder="Note areas to improve..."
              rows={3}
            />
          </div>

          <button className="save-button" onClick={handleSave}>Save Summary</button>
        </div>
      </div>
    </div>
  );
};
